import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { ReportId, ReportPayload } from './types';

/**
 * One query per section — four independent reads, never one composite.
 *
 * That is what gives the page its per-section error boundary: `backend-finance`
 * being unreachable takes the revenue band down and leaves the other three
 * usable, and a panel-level failure never blanks the page.
 *
 * The key mirrors the REST URL, so every mutation elsewhere in the console that
 * moves one of these figures — a publish, a moderation decision, a role change,
 * a plan edit — invalidates the exact tuple the section registered.
 */
export function reportKey(report: ReportId) {
  return key('analytics', 'report', report);
}

export function useReport(report: ReportId) {
  return useQuery({
    queryKey: reportKey(report),
    queryFn: () => api.get<ReportPayload>('analytics', `/admin/reports/${report}`),
    // A figure is a state of the world now, and an operator returning to the tab
    // wants the current one.
    staleTime: STALE_TIME.list,
  });
}
